import React from 'react';
import { casillaText, formatMoney, sumBy } from './taxModel';

// Replica of the DGI "Declaración Jurada del ITBMS - F430V6" as filed on e-Tax 2.0.
// Same layout and casilla numbers, so the accountant can compare it box by box.

const th = 'border border-neutral-400 bg-neutral-100 px-1.5 py-1 text-left text-[9px] font-semibold uppercase tracking-wide text-neutral-700';
const tdLabel = 'border border-neutral-300 px-1.5 py-0.5 text-[9.5px] leading-tight text-neutral-900';
const tdNo = 'w-8 border border-neutral-300 bg-neutral-50 px-1 py-0.5 text-center text-[9px] text-neutral-600';
const tdAmount = 'border border-neutral-300 px-1.5 py-0.5 text-right text-[10px] tabular-nums text-neutral-950';

function Box({ form, name, adjusted, className = tdAmount }) {
  const isAdjusted = adjusted.has(name);
  return (
    <td className={`${className} ${isAdjusted ? 'bg-amber-50 print:bg-transparent' : ''}`} data-casilla={name}>
      {casillaText(form, name)}
      {isAdjusted && <span className="no-print ml-1 align-super text-[8px] text-amber-600" title="Incluye un ajuste manual">●</span>}
    </td>
  );
}

function No({ children }) {
  return <td className={tdNo}>{children}</td>;
}

function SalesRow({ form, adjusted, label, rate, amountKey, taxKey }) {
  return (
    <tr>
      <td className={tdLabel}>{label}</td>
      <td className={`${tdLabel} w-12 text-center`}>{rate}</td>
      <No>{amountKey}</No>
      <Box form={form} name={amountKey} adjusted={adjusted} />
      <No>{taxKey || ''}</No>
      {taxKey ? <Box form={form} name={taxKey} adjusted={adjusted} /> : <td className={`${tdAmount} bg-neutral-100`} />}
    </tr>
  );
}

const PURCHASE_BLOCKS = [
  { label: 'Compras y prestación de servicios e importaciones gravadas imputables directamente a operaciones gravadas', rows: [22, 23, 24], total: ['25', '253'] },
  { label: 'Compras y prestación de servicios e importaciones gravadas imputables a operaciones mixtas', rows: [26, 27, 28], total: ['29', '293'] },
  { label: 'Compras y prestación de servicios e importaciones gravadas destinadas directamente a exportaciones, reexportaciones establecidas en el parágrafo 8 literal a) num. 2 del art. 1057v', rows: [30, 31, 32], total: ['33', '333'] },
  { label: 'Compras y prestación de servicios e importaciones gravadas destinadas directamente a operaciones exentas establecidas en el parágrafo 8 del artículo 1057v, con excepción del literal a) num. 2', rows: [34, 35, 36], total: ['37', '373'] },
];
const RATE_OF = { 0: '7%', 1: '10%', 2: '15%' };

function PurchaseBlock({ block, form, adjusted }) {
  return (
    <>
      {block.rows.map((row, index) => (
        <tr key={row}>
          {index === 0 && <td className={tdLabel} rowSpan={4}>{block.label}</td>}
          <td className={`${tdLabel} w-10 text-center`}>{RATE_OF[index]}</td>
          <No>{row}</No>
          <Box form={form} name={String(row)} adjusted={adjusted} />
          <No>{`${row}1`}</No>
          <Box form={form} name={`${row}1`} adjusted={adjusted} />
          <No>{`${row}2`}</No>
          <Box form={form} name={`${row}2`} adjusted={adjusted} />
          <No>{`${row}3`}</No>
          <Box form={form} name={`${row}3`} adjusted={adjusted} />
        </tr>
      ))}
      <tr>
        <td className={`${tdLabel} text-center font-semibold`}>Total</td>
        <No>{block.total[0]}</No>
        <td className={`${tdAmount} bg-neutral-100`} />
        <td className={`${tdNo} bg-neutral-100`} colSpan={4} />
        <No>{block.total[1]}</No>
        <Box form={form} name={block.total[1]} adjusted={adjusted} />
      </tr>
    </>
  );
}

function ListRow({ form, adjusted, label, no }) {
  return (
    <tr>
      <td className={tdLabel}>{label}</td>
      <No>{no}</No>
      <Box form={form} name={no} adjusted={adjusted} />
    </tr>
  );
}

const SALES_ROWS = [
  ['Operaciones gravadas del período (ventas y prestación de servicios)', '7%', '11', '111'],
  ['Operaciones gravadas del período (transporte aéreo de pasajeros local e internacional)', '7%', '12', '121'],
  ['Operaciones gravadas del período (bebidas alcohólicas)', '10%', '13', '131'],
  ['Servicios gravados del período (hospedaje o alojamiento público)', '10%', '14', '141'],
  ['Operaciones gravadas del período (cigarrillos)', '15%', '15', '151'],
  ['Operaciones de exportación y reexportación de bienes gravados y no gravados (parágrafo 8, art. 1057v del Código Fiscal, literal a) num. 2)', '', '16', null],
  ['Otras operaciones exentas (parágrafo 8, art. 1057v del Código Fiscal, con excepción literal a) num. 2) (ventas y prestación de servicios)', '', '17', null],
  ['Operaciones no gravadas (parágrafo 7, art. 1057 del Código Fiscal) (ventas y prestación de servicios)', '', '18', null],
  ['Total de operaciones del período y subtotal débito fiscal', '', '19', '191'],
  ['Devoluciones y descuentos en compras gravadas (180 días de facturación)', '', '20', '201'],
];

const CREDIT_LEFT = [
  ['Crédito fiscal no deducible', '41', null],
  ['CRÉDITO FISCAL DEDUCIBLE POR OPERACIONES GRAVADAS', null, 'heading'],
  ['Crédito fiscal directo operaciones gravadas', '42'],
  ['Crédito fiscal proporcional operaciones gravadas', '43'],
  ['Crédito fiscal devol. y descuentos en ventas gravadas', '44'],
  ['Crédito fiscal deducible operaciones gravadas del período', '45'],
  ['CRÉDITO FISCAL DEDUCIBLE POR OPERACIONES AL EXTERIOR', null, 'heading'],
  ['Crédito fiscal directo operaciones exportación', '46'],
  ['Crédito fiscal proporcional operaciones exportación', '47'],
  ['Crédito fiscal deducible por operaciones al exterior susceptible de CPC', '48'],
  ['INFORMACIÓN ADICIONAL PARA CPC', null, 'heading'],
  ['Crédito fiscal susceptible CPC', '60'],
  ['Crédito fiscal susceptible CPC acumulado', '61'],
  ['Menos CPC otorgado por resolución', '62'],
  ['Total crédito fiscal susceptible CPC', '63'],
];

const SETTLEMENT = [
  ['Débito fiscal', '49'],
  ['Crédito fiscal deducible operaciones gravadas del período', '50'],
  ['Saldo a favor del contribuyente declaración anterior', '51'],
  ['Crédito de retención del ITBMS. Ver instructivo al dorso', '52'],
  ['Crédito fiscal deducible por operaciones al exterior. Ver instructivo al dorso', '53'],
  ['Saldo a favor del contribuyente que se trasladará al siguiente período fiscal', '54'],
  ['Impuesto a pagar', '55'],
  ['Recargo', '56'],
  ['Multa', '57'],
  ['Interés', '58'],
  ['Total a pagar', '59'],
];

const SWORN = 'El contribuyente, bajo gravedad de juramento, declara que imputar un costo, gasto o ingreso tanto de fuente panameña como de fuente extranjera o exenta, lo hace cumpliendo con todas las disposiciones legales, reglamentarias y de cualquier tipo, vigentes en la República de Panamá, incluyendo todas las retenciones sobre los pagos efectuados de manera directa, a través de interpuesta persona o por cualquier otra vía, a beneficiarios radicados en el exterior.';

const dmy = (iso) => (iso ? iso.split('-').reverse().join('/') : '');

export function Form430Sheet({ report }) {
  const { form, taxpayer, adjustments } = report;
  const adjusted = new Set(Object.keys(adjustments || {}));
  const noName = (report.purchases || []).filter((row) => row.reason === 'sin_datos_empresa');
  const generated = new Date(report.generated_at).toLocaleString('es-PA');

  return (
    <div className="mx-auto w-full min-w-[720px] max-w-[210mm] bg-white p-4 font-sans text-neutral-950 shadow-lg print:max-w-none print:p-0 print:shadow-none" data-testid="form-430-sheet">
      <div className="mb-2 border border-amber-500 bg-amber-50 px-2 py-1 text-[9px] text-amber-900">
        <strong>Borrador de trabajo</strong> generado por Valhalla ERP el {generated}. No es la declaración oficial: la contadora la verifica y la
        presenta en e-Tax 2.0 de la DGI.{report.frozen && ' Las cifras mostradas son las registradas al marcar el período como presentado.'}
      </div>

      {noName.length > 0 && (
        <div className="mb-2 border-2 border-red-600 bg-red-50 px-2 py-1 text-[9.5px] font-semibold leading-snug text-red-900" data-testid="no-company-name-notice">
          ATENCIÓN: {noName.length} compra(s) con ITBMS por B/. {formatMoney(sumBy(noName, 'tax'))} tienen la factura sin el nombre ni el RUC de la empresa.
          Son gastos de la empresa, pero NO dan crédito fiscal y no están en las casillas de crédito. Ver el anexo (hoja 2).
        </div>
      )}

      <div className="flex items-end justify-between border-b-2 border-neutral-800 pb-1">
        <div>
          <p className="text-[11px] font-bold leading-tight">Declaración Jurada del Impuesto a la Transferencia de Bienes Corporales Muebles y la Prestación de Servicios — F430V6</p>
          <p className="text-[10px] font-semibold">DIRECCIÓN GENERAL DE INGRESOS</p>
        </div>
        {report.presented && <p className="text-right text-[9px]">No. {report.presented.declaration_number}<br />{dmy(report.presented.filed_date)}</p>}
      </div>

      <table className="mt-1 w-full border-collapse text-[9.5px]">
        <tbody>
          <tr>
            <td className={tdLabel}><b>RUC</b><br />{taxpayer.ruc}</td>
            <td className={tdLabel}><b>DV</b><br />{taxpayer.dv}</td>
            <td className={tdLabel} colSpan={2}><b>Razón social</b><br />{taxpayer.legal_name}</td>
            <td className={tdLabel}><b>Período declarado</b><br />{report.period}</td>
            <td className={tdLabel}><b>Tipo presentación</b><br />Original</td>
          </tr>
          <tr>
            <td className={tdLabel}><b>Período inicial</b><br />{dmy(report.start)}</td>
            <td className={tdLabel}><b>Período final</b><br />{dmy(report.end)}</td>
            <td className={tdLabel}><b>Tipo declaración</b><br />0-Normal</td>
            <td className={tdLabel}><b>Parágrafo 9-A</b><br />{taxpayer.paragraph_9a ? 'SÍ' : 'NO'}</td>
            <td className={tdLabel}><b>Vence</b><br />{dmy(report.due_date)}</td>
            <td className={tdLabel}><b>Estado</b><br />{report.status === 'presented' ? 'Presentada' : 'Borrador'}</td>
          </tr>
        </tbody>
      </table>

      <p className="mt-2 text-[10px] font-bold uppercase">Operaciones y prestaciones de servicios del período y débito fiscal</p>
      <table className="w-full border-collapse">
        <thead>
          <tr><th className={th}>Ventas, servicios y exportaciones</th><th className={th}>Tarifa</th><th className={th} colSpan={2}>Monto</th><th className={th} colSpan={2}>Impuesto causado</th></tr>
        </thead>
        <tbody>
          {SALES_ROWS.map(([label, rate, amountKey, taxKey]) => (
            <SalesRow key={amountKey} form={form} adjusted={adjusted} label={label} rate={rate} amountKey={amountKey} taxKey={taxKey} />
          ))}
          <tr>
            <td className={`${tdLabel} font-semibold`} colSpan={4}>Total débito fiscal</td>
            <No>211</No>
            <Box form={form} name="211" adjusted={adjusted} className={`${tdAmount} font-bold`} />
          </tr>
        </tbody>
      </table>

      <p className="mt-2 text-[10px] font-bold uppercase">Compras o adquisiciones, prestación de servicios, importaciones e ITBMS cargado</p>
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className={th} rowSpan={2}>Compras y prestación de servicios locales e importaciones gravadas</th>
            <th className={th} rowSpan={2}>Tarifa</th>
            <th className={th} colSpan={2}>Locales — monto</th>
            <th className={th} colSpan={2}>Importaciones — monto</th>
            <th className={th} colSpan={2}>Total — monto</th>
            <th className={th} colSpan={2}>ITBMS cargado</th>
          </tr>
        </thead>
        <tbody>
          {PURCHASE_BLOCKS.map((block) => <PurchaseBlock key={block.total[1]} block={block} form={form} adjusted={adjusted} />)}
          <tr>
            <td className={`${tdLabel} font-semibold`} colSpan={2}>Totalice el monto y el ITBMS cargado</td>
            <No>38</No><Box form={form} name="38" adjusted={adjusted} />
            <No>381</No><Box form={form} name="381" adjusted={adjusted} />
            <No>382</No><Box form={form} name="382" adjusted={adjusted} />
            <No>383</No><Box form={form} name="383" adjusted={adjusted} />
          </tr>
          <tr>
            <td className={tdLabel} colSpan={2}>Compras e importaciones exentas</td>
            <No>39</No><Box form={form} name="39" adjusted={adjusted} />
            <No>391</No><Box form={form} name="391" adjusted={adjusted} />
            <No>392</No><Box form={form} name="392" adjusted={adjusted} />
            <td className={`${tdNo} bg-neutral-100`} colSpan={2} />
          </tr>
          <tr>
            <td className={tdLabel} colSpan={6}>Devoluciones y descuentos en ventas gravadas (hasta 180 días de facturación)</td>
            <No>402</No><Box form={form} name="402" adjusted={adjusted} />
            <No>403</No><Box form={form} name="403" adjusted={adjusted} />
          </tr>
        </tbody>
      </table>

      <div className="mt-2 grid grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase">Determinación del crédito fiscal</p>
          <table className="w-full border-collapse">
            <tbody>
              {CREDIT_LEFT.map(([label, no, kind]) => (kind === 'heading'
                ? <tr key={label}><td className="border border-neutral-300 bg-neutral-100 px-1.5 py-0.5 text-[9px] font-bold" colSpan={3}>{label}</td></tr>
                : <ListRow key={no} form={form} adjusted={adjusted} label={label} no={no} />))}
            </tbody>
          </table>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase">Liquidación y saldo del impuesto</p>
          <table className="w-full border-collapse">
            <tbody>
              {SETTLEMENT.map(([label, no]) => (
                <tr key={no} className={['55', '59'].includes(no) ? 'font-bold' : ''}>
                  <td className={tdLabel}>{label}</td>
                  <No>{no}</No>
                  <Box form={form} name={no} adjusted={adjusted} />
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-[10px] font-bold uppercase">Persona autorizada o representante legal</p>
          <table className="w-full border-collapse">
            <tbody>
              <tr><td className={`${tdLabel} w-14`}>Nombre</td><td className={tdLabel}>{taxpayer.representative_name}</td></tr>
              <tr><td className={tdLabel}>Cédula</td><td className={tdLabel}>{taxpayer.representative_id}</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <p className="mt-2 text-[7.5px] leading-snug text-neutral-600">{SWORN}</p>

      <div className="mt-4 grid grid-cols-3 gap-4 text-[9px]">
        {['Preparado por', 'Verificado por (contador/a)', 'Representante legal'].map((who) => (
          <div key={who}>
            <div className="h-9 border-b border-neutral-700" />
            <p className="mt-0.5 font-semibold">{who}</p>
            <p className="text-neutral-500">Nombre, idoneidad / cédula y fecha</p>
          </div>
        ))}
      </div>
      {report.notes && <p className="mt-2 border-t border-neutral-300 pt-1 text-[9px]"><b>Notas del período:</b> {report.notes}</p>}
    </div>
  );
}
